// components/campaigns/CampaignsList.js
import React, { useState } from 'react';
import { ContentLayout, PageContainer, ContentSection } from '../layout';
import { Loading, ErrorMessage, Input } from '../common';
import CampaignCard from './CampaignCard';
import { CreateCampaignModal } from '../modals/CampaignModal';
import { useCampaigns } from '../../hooks/useCampaigns';
import { useTranslations } from '../../hooks/useTranslations';
import TemplateManager from '../TemplateManager';
import CreditPurchase from '../credits/CreditPurchase';

export function CampaignsList({ onNavigate }) {
  const t = useTranslations();
  const {
    campaigns,
    loading,
    error,
    createCampaign,
    updateCampaign,
    toggleCampaign,
    deleteCampaign,
    launchCampaign,
    clearError
  } = useCampaigns();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [operationLoading, setOperationLoading] = useState(false);

  // Filter campaigns based on search query
  const filteredCampaigns = campaigns.filter(campaign => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      campaign.name.toLowerCase().includes(query) ||
      campaign.targetAudience.toLowerCase().includes(query) ||
      campaign.messageContent.toLowerCase().includes(query)
    );
  });

  // Handle opening create modal
  const handleCreateNew = () => {
    setEditingCampaign(null);
    setIsModalOpen(true);
  };

  // Handle opening edit modal
  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingCampaign(null);
  };

  // Handle form submission
  const handleSubmit = async (formData) => {
    setOperationLoading(true);
    try {
      let result;
      if (editingCampaign) {
        result = await updateCampaign(editingCampaign.id, formData);
      } else {
        result = await createCampaign(formData);
      }

      if (result.success) {
        setIsModalOpen(false);
        setEditingCampaign(null);
      }

      return result;
    } finally {
      setOperationLoading(false);
    }
  };

  // Handle status toggle
  const handleToggleStatus = async (campaignId) => {
    setOperationLoading(true);
    try {
      await toggleCampaign(campaignId);
    } finally {
      setOperationLoading(false);
    }
  };
  const handleDelete = async (campaignId) => {
    setOperationLoading(true);
    try {
      await deleteCampaign(campaignId);
    } finally {
      setOperationLoading(false);
    }
  };
  const handleLaunchCampaign = async (campaignId) => {
    setOperationLoading(true);
    try {
      const result = await launchCampaign(campaignId);
      if (result.success) {
        // Show success message (optional)
        console.log('Campaign launched successfully:', result.result);
        // You could add a toast notification here if you have one
      }
      return result;
    } finally {
      setOperationLoading(false);
    }
  };

  if (loading && campaigns.length === 0) {
    return (
      <ContentLayout
        currentPage="campaigns"
        title={t.campaigns}
        subtitle={t.manageAppointmentBookings}
      >
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      currentPage="campaigns"
      title={t.campaigns}
      description={t.manageAppointmentBookings}
      onNavigate={onNavigate}
      primaryAction={{
        label: t.newCampaign,
        onClick: handleCreateNew,
        variant: "primary",
        className: "bg-orange-600 hover:bg-orange-700",
        disabled: operationLoading,
        icon: (
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        )
      }}
    >
      <PageContainer>
        <ContentSection>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mt-4 md:mt-0">
            {/* Error handling */}
            {error && (
              <ErrorMessage
                message={error}
                onDismiss={clearError}
                className="mb-6"
              />
            )}

            {/* Search bar and Credit Purchase - horizontally stacked */}
            <div className="mb-6">
              <div className="flex flex-col-reverse lg:flex-row gap-4 items-stretch lg:items-center justify-between">
                <div className="flex-1 w-full lg:w-auto">
                  <Input
                    type="text"
                    placeholder={t.searchCampaigns}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full py-3"
                    leftIcon={
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    }
                  />
                </div>
                <div className="flex-shrink-0 w-full lg:w-auto flex items-center justify-center lg:justify-start">
                  <CreditPurchase />
                </div>
              </div>
            </div>

          {/* Campaigns grid */}
          {filteredCampaigns.length === 0 ? (
            <div className="text-center py-12">
              {campaigns.length === 0 ? (
                // No campaigns at all
                <div>
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t.noCampaignsYet}</h3>
                  <p className="text-gray-500 mb-6">
                    {t.createFirstMarketingCampaign}
                  </p>
                  <button
                    onClick={handleCreateNew}
                    className="rounded-xl bg-orange-600 text-white px-6 py-2.5 hover:bg-orange-700 transition-colors shadow-sm"
                  >
                    {t.createFirstCampaign}
                  </button>
                </div>
              ) : (
                // No search results
                <div>
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t.noCampaignsFound}</h3>
                  <p className="text-gray-500">
                    {t.adjustSearchTermsOrCreate}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onLaunchCampaign={handleLaunchCampaign}
                  onUpdateCampaign={updateCampaign}
                />
              ))}
            </div>
          )}

            {/* Loading overlay */}
            {operationLoading && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
                  <div className='ml-2'>
                    <Loading />
                  </div>
                  <span className="text-gray-600">המתן בזמן שהפעולה מתבצעת...</span>
                </div>
              </div>
            )}
          </div>

          <TemplateManager />

        </ContentSection>
      </PageContainer>

      {/* Create/Edit Campaign Modal */}
      <CreateCampaignModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        campaign={editingCampaign}
        mode={editingCampaign ? 'edit' : 'add'}
      />
    </ContentLayout>
  );
}